import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { FormInput, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useRecordPayment, type PaymentMethod } from './hooks/useBilling';
import { useToast } from '@/components/ui/Toast';

// ─── Validation Schema ────────────────────────────────────────────────────────

function createPaymentSchema(maxAmount: number) {
  return z.object({
    amount: z.coerce
      .number()
      .min(0.01, 'Amount must be greater than 0')
      .max(maxAmount, `Amount cannot exceed outstanding balance ($${maxAmount.toFixed(2)})`),
    paymentMethod: z.enum(['bank_transfer', 'cheque', 'credit_card', 'cash', 'other'], {
      required_error: 'Payment method is required',
    }),
    paymentDate: z.string().min(1, 'Payment date is required'),
    referenceNumber: z.string().max(100).optional(),
    notes: z.string().max(500).optional(),
  });
}

type PaymentFormValues = z.infer<ReturnType<typeof createPaymentSchema>>;

const PAYMENT_METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

interface PaymentRecordFormProps {
  invoiceId: string;
  outstandingBalance: number;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * PaymentRecordForm — modal form for recording payments against an invoice.
 * Validates amount does not exceed outstanding balance.
 * Requirements: 10.18
 */
export function PaymentRecordForm({
  invoiceId,
  outstandingBalance,
  onSuccess,
  onCancel,
}: PaymentRecordFormProps) {
  const { toast } = useToast();
  const recordPaymentMutation = useRecordPayment();

  const schema = useMemo(() => createPaymentSchema(outstandingBalance), [outstandingBalance]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<PaymentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: outstandingBalance,
      paymentMethod: undefined,
      paymentDate: new Date().toISOString().split('T')[0],
      referenceNumber: '',
      notes: '',
    },
  });

  const onSubmit = async (values: PaymentFormValues) => {
    try {
      await recordPaymentMutation.mutateAsync({
        invoiceId,
        amount: values.amount,
        paymentMethod: values.paymentMethod as PaymentMethod,
        paymentDate: values.paymentDate,
        referenceNumber: values.referenceNumber || undefined,
        notes: values.notes || undefined,
      });
      toast({ variant: 'success', title: 'Payment recorded successfully' });
      onSuccess();
    } catch {
      toast({ variant: 'error', title: 'Failed to record payment' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      {/* Outstanding Balance Info */}
      <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
        <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
          Outstanding Balance
        </p>
        <p className="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">
          ${outstandingBalance.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
        </p>
      </div>

      {/* Amount */}
      <FormInput
        name="amount"
        control={control}
        label="Payment Amount"
        type="number"
        min={0.01}
        max={outstandingBalance}
        step="0.01"
        required
        placeholder="0.00"
      />

      {/* Payment Method */}
      <FormSelect
        name="paymentMethod"
        control={control}
        label="Payment Method"
        required
        options={PAYMENT_METHOD_OPTIONS}
        placeholder="Select payment method"
      />

      {/* Payment Date */}
      <FormInput name="paymentDate" control={control} label="Payment Date" type="date" required />

      {/* Reference Number */}
      <FormInput
        name="referenceNumber"
        control={control}
        label="Reference Number"
        placeholder="e.g., cheque number, transaction ID"
      />

      {/* Notes */}
      <FormTextarea
        name="notes"
        control={control}
        label="Notes"
        placeholder="Optional notes about this payment..."
        rows={2}
      />

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          Record Payment
        </Button>
      </div>
    </form>
  );
}
